import PropTypes from 'prop-types';
import { useState, memo, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import TabPanel from '../../../../TabPanel';
import Filter from '../../../../Filters/Filter';
import Sliders from './Sliders';
import { flightSearch } from '../../../../../store/slices/flight';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { FlightSearchTypes } from '../../../../../constants';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';
import getFlightRouteLabel from '../../../../../helpers/getFlightRouteLabel';

const TimeFlightFilters = ({ tripId, variant, filterSpacingClass }) => {
  const { track } = useAnalytics();
  const dispatch = useDispatch();
  const isTripSearch = !tripId;
  const [selectedTab, setSelectedTab] = useState(0);
  const { searchObj, defaultSearchObj } = useSelector(flightSearchInput(tripId, isTripSearch));

  const doShowFilter =
    !_.isEmpty(defaultSearchObj) &&
    !_.isEmpty(searchObj.filterTakeoff) &&
    !_.isEmpty(searchObj.filterTakeoff);

  const isFilterEmpty = useMemo(() => {
    if (doShowFilter) {
      if (_.isEmpty(searchObj.filterTakeoff) && _.isEmpty(searchObj.filterLanding)) return true;

      const hasTakeoffChange =
        _.map(searchObj.filterTakeoff, (t) => `${t.start}-${t.end}`).join('/') !==
        _.map(defaultSearchObj.takeoff, (t) => `${t.min}-${t.max}`).join('/');

      const hasLandingChange =
        _.map(searchObj.filterLanding, (t) => `${t.start}-${t.end}`).join('/') !==
        _.map(defaultSearchObj.landing, (t) => `${t.min}-${t.max}`).join('/');

      return !hasLandingChange && !hasTakeoffChange;
    }
    return false;
  }, [
    defaultSearchObj.landing,
    defaultSearchObj.takeoff,
    doShowFilter,
    searchObj.filterLanding,
    searchObj.filterTakeoff,
  ]);

  // Build route label for each segment tab
  const getRouteLabel = useCallback(
    (index) =>
      getFlightRouteLabel({
        flightSegments: searchObj.flightSegments,
        flightSearchType: searchObj.flightSearchType,
        segmentIndex: index,
      }),
    [searchObj.flightSegments, searchObj.flightSearchType],
  );

  if (!doShowFilter) return <></>;

  const handleTabChange = (index) => {
    setSelectedTab(index);
    track('timeFlightFilters.handleTabChange', {
      index,
    });
  };

  const search = (o) => {
    const payload = {
      tripId,
      searchObj: {
        ...o,
        page: 1,
      },
    };

    if (isTripSearch) {
      dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
      dispatch(flightSearch({ isTripSearch: true, ...payload.searchObj }));
    } else dispatch(flightSearch(payload));
    track('timeFlightFilters.search', {
      searchObj: JSON.stringify(o),
    });
  };

  const handleChangeTakeoff = (event, newValue) => {
    const filterTakeoff = {
      ...(searchObj.filterTakeoff || {}),
    };
    filterTakeoff[selectedTab] = {
      start: newValue[0],
      end: newValue[1],
    };
    search({
      filterTakeoff: {
        ...filterTakeoff,
      },
    });
  };

  const handleChangeLanding = (event, newValue) => {
    const filterLanding = {
      ...(searchObj.filterLanding || {}),
    };
    filterLanding[selectedTab] = {
      start: newValue[0],
      end: newValue[1],
    };
    search({
      filterLanding: {
        ...filterLanding,
      },
    });
  };

  const handleClear = () => {
    search({
      filterTakeoff: {},
      filterLanding: {},
    });
  };

  const handleClose = () => {
    setSelectedTab(0);
  };

  const isTakeoffEmpty = _.isEmpty(searchObj.filterTakeoff);
  const isLandingEmpty = _.isEmpty(searchObj.filterLanding);
  const takeoffLength = Object.keys(defaultSearchObj.takeoff || {}).length;

  return (
    <Filter
      label="Filter by time"
      menuWidthClass="w-max"
      isFilterEmpty={isFilterEmpty}
      variant={variant}
      filterMaxWidthClass="max-w-full"
      filterSpacingClass={filterSpacingClass}
      handleClear={handleClear}
      onClose={handleClose}
      bgClassName={!isTripSearch ? '' : undefined}
    >
      {takeoffLength > 1 && (
        <div
          className={clsx({
            'border-b-2 grid grid-cols-2 w-full': takeoffLength <= 2,
            'border-b-2 grid grid-cols-3 w-full': takeoffLength >= 3,
          })}
        >
          {defaultSearchObj.takeoff &&
            Object.values(defaultSearchObj.takeoff).map((takeoff, index) => (
              <a
                onClick={() => {
                  handleTabChange(index);
                }}
                onKeyDown={() => {
                  handleTabChange(index);
                }}
                role="button"
                tabIndex={index}
                className={clsx(
                  selectedTab === index && 'text-theme border-b-2 border-theme',
                  'text-center py-2 px-5 text-gray-700 cursor-pointer',
                )}
                key={`${index.toString()}`}
              >
                {searchObj.flightSearchType === FlightSearchTypes.MULTI_CITY ? (
                  <>Flight {index + 1}</>
                ) : (
                  <>{index === 0 ? 'Outbound' : 'Return'}</>
                )}
              </a>
            ))}
        </div>
      )}
      <div className="p-5" style={{ minWidth: '320px' }}>
        {defaultSearchObj.takeoff &&
          Object.values(defaultSearchObj.takeoff).map((takeoff, index) => (
            <TabPanel value={selectedTab} index={index} key={`${index.toString()}`}>
              <Sliders
                takeoff={[
                  !isTakeoffEmpty && +_.keys(searchObj.filterTakeoff)[0] === index
                    ? searchObj.filterTakeoff[index].start
                    : defaultSearchObj.takeoff[index].start,
                  !isTakeoffEmpty && +_.keys(searchObj.filterTakeoff)[0] === index
                    ? searchObj.filterTakeoff[index].end
                    : defaultSearchObj.takeoff[index].end,
                ]}
                landing={[
                  !isLandingEmpty && +_.keys(searchObj.filterLanding)[0] === index
                    ? searchObj.filterLanding[index].start
                    : defaultSearchObj.landing[index].start,
                  !isLandingEmpty && +_.keys(searchObj.filterLanding)[0] === index
                    ? searchObj.filterLanding[index].end
                    : defaultSearchObj.landing[index].end,
                ]}
                onTakeOffChange={handleChangeTakeoff}
                onLandingChange={handleChangeLanding}
                landingMin={defaultSearchObj.landing[index].min}
                landingMax={defaultSearchObj.landing[index].max}
                takeoffMin={defaultSearchObj.takeoff[index].min}
                takeoffMax={defaultSearchObj.takeoff[index].max}
                routeLabel={getRouteLabel(index)}
                isReturn={searchObj.flightSearchType !== FlightSearchTypes.MULTI_CITY && index > 0}
                key={`${index.toString()}`}
              />
            </TabPanel>
          ))}
      </div>
    </Filter>
  );
};

TimeFlightFilters.propTypes = {
  tripId: PropTypes.string,
  variant: PropTypes.string,
  filterSpacingClass: PropTypes.string,
};

TimeFlightFilters.defaultProps = {
  tripId: undefined,
  variant: undefined,
  filterSpacingClass: undefined,
};
export default memo(TimeFlightFilters);
